/**
 * Leveranciers — freelancers & bedrijven, overeenkomsten, datacorrecties en het
 * beoordelingendashboard (gap-analyse #7 en #9, 26 sep 2026).
 *
 * Sinds 23 okt: het overzicht (alleen lezen, met inzet en beoordelingen per kwartaal) is
 * er ook voor PL's (recht 'leveranciers'); de HR-tabbladen blijven achter hr_leveranciers.
 *
 * Airtable (Leveranciers-base) blijft de bron: lezen uit de engine-spiegel, schrijven
 * (nieuw contract, datacorrectie verwerken) via de engine naar Airtable. Op test staat
 * schrijven uit; de knoppen zeggen dat dan.
 */
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Button, Tab, Tabs } from '@mui/material';
import toast from 'react-hot-toast';
import PageHeader from '../../ui-components/PageHeader';
import { BACKEND_URL } from '../../utils/apiConfig';
import { useUserRole } from '../../contexts/UserRoleContext';
import { meldFout } from '../../utils/apiFout';
import { ACCENT, INK, haalJson } from './hrUi';
import LeveranciersOverzicht from './LeveranciersOverzicht';
import Overeenkomsten from './Overeenkomsten';
import Datacorrecties from './Datacorrecties';
import BeoordelingenDashboard from './BeoordelingenDashboard';
import Geheimhouding from './Geheimhouding';

const TABS = [
  { key: 'overzicht', label: 'Freelancers & bedrijven', component: LeveranciersOverzicht, feature: 'leveranciers' },
  { key: 'overeenkomsten', label: 'Overeenkomsten', component: Overeenkomsten, feature: 'hr_leveranciers' },
  { key: 'datacorrecties', label: 'Datacorrecties', component: Datacorrecties, feature: 'hr_leveranciers' },
  // Geheimhouding freelancers (23 okt, klantvereisten Schneider): zelfde recht als de overeenkomsten.
  { key: 'geheimhouding', label: 'Geheimhouding', component: Geheimhouding, feature: 'hr_leveranciers' },
  // Zelfde recht als het bestaande overzicht-endpoint (admin_settings).
  { key: 'beoordelingen', label: 'Beoordelingen', component: BeoordelingenDashboard, feature: 'admin_settings' },
];

export default function Leveranciers() {
  const { permissions } = useUserRole();
  const magBewerken = !!permissions?.hr_leveranciers?.edit;
  const tabs = TABS.filter((t) => !t.feature || permissions?.[t.feature]?.view);
  const [params, setParams] = useSearchParams();
  const actief = tabs.find((t) => t.key === params.get('tab')) || tabs[0];
  const Inhoud = actief?.component;
  const [versie, setVersie] = useState(0);
  const [bezig, setBezig] = useState(false);

  // Spiegel nu verversen (anders 's nachts). Alleen-lezen voor Airtable, dus geen vangnet.
  const ververs = async () => {
    setBezig(true);
    try {
      const d = await haalJson(`${BACKEND_URL}/Api/leveranciers/ververs`, 'Verversen', { method: 'POST' });
      if (d.overgeslagen) toast(d.reden);
      else toast.success('Bijgewerkt uit Airtable');
      setVersie((v) => v + 1);
    } catch (e) {
      meldFout(e, 'Verversen');
    } finally {
      setBezig(false);
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      <PageHeader
        icon="handshake"
        title="Leveranciers"
        subtitle="Freelancers en bedrijven waar we mee werken — contact, tarieven, beoordelingen en inzet"
        accent={ACCENT}
        rightActions={magBewerken && (
          <Button onClick={ververs} disabled={bezig} sx={{ textTransform: 'none' }}>
            {bezig ? 'Bezig…' : 'Nu bijwerken uit Airtable'}
          </Button>
        )}
      />
      {tabs.length > 1 && <Tabs value={actief.key} onChange={(_e, v) => setParams(v === tabs[0].key ? {} : { tab: v })}
        variant="scrollable" allowScrollButtonsMobile
        sx={{ mb: 3, borderBottom: `1px solid ${INK.lijn}`, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}>
        {tabs.map((t) => <Tab key={t.key} value={t.key} label={t.label} />)}
      </Tabs>}
      {Inhoud && <Inhoud key={versie} magBewerken={magBewerken} />}
    </Box>
  );
}
